import React, { useState } from 'react';
import Breadcrumbs from '../../components/ui/Breadcrumbs.tsx';
import CredencialesApiPanel from './CredencialesApiPanel.tsx';
import { ApiConnectionCredentials, EMPTY_API_CREDENTIALS } from './apiCredentials.types.ts';
import { Search, Code2, Sparkles, Building2, FileSignature, ArrowRight, ShieldCheck, Activity, CheckCircle2, Link2 } from 'lucide-react';

interface SubApiCard {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  status: 'active' | 'beta' | 'soon';
  statusText: string;
  targetPage: string;
  color: string;
}

const SUB_APIS: SubApiCard[] = [
  {
    id: 'ariba-test',
    title: 'Ariba API Tester',
    description: 'Prueba los endpoints de Ariba configurados para este cliente en Configuración (Supplier Data, Contract Workspaces, etc.).',
    icon: Link2,
    status: 'active',
    statusText: 'Activo',
    targetPage: 'ariba-api-test',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 's4hana',
    title: 'SAP S/4HANA Cloud APIs',
    description: 'Pruebas de endpoints OData y SOAP (Business Partner, Purchase Order, Sales Order).',
    icon: Building2,
    status: 'beta',
    statusText: 'Beta',
    targetPage: 's4-apis',
    color: 'from-indigo-500 to-purple-500'
  },
  {
    id: 'docusign',
    title: 'Docusign APIs',
    description: 'Firma digital, creación de sobres, descarga de documentos y rastreo del ciclo de vida.',
    icon: FileSignature,
    status: 'beta',
    statusText: 'Beta',
    targetPage: 'docusign-apis',
    color: 'from-purple-500 to-pink-500'
  }
];

interface PruebasApisHomeProps {
  setPage: (page: string) => void;
  embedded?: boolean;
  clienteId?: string;
  clienteNombre?: string;
  credentials?: ApiConnectionCredentials;
  onCredentialsChange?: (credentials: ApiConnectionCredentials) => void;
}

export default function PruebasApisHome({
  setPage,
  embedded = false,
  clienteId,
  clienteNombre,
  credentials = EMPTY_API_CREDENTIALS,
  onCredentialsChange = () => {}
}: PruebasApisHomeProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredApis = SUB_APIS.filter(
    (api) =>
      api.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      api.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={embedded ? 'text-white' : 'min-h-screen p-4 md:p-8 text-white bg-transparent relative overflow-hidden'}>
      {/* Luces de fondo decorativas */}
      {!embedded && (
        <>
          <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-blue-900/30 rounded-full blur-[120px] pointer-events-none z-0" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-purple-900/20 rounded-full blur-[100px] pointer-events-none z-0" />
        </>
      )}

      <div className={embedded ? '' : 'relative z-10 max-w-7xl mx-auto'}>
        {!embedded && <Breadcrumbs currentPage="pruebas-apis" setPage={setPage} />}

        {/* HEADER — "Mis APIs" cuando se entra desde el Home (sin proyecto,
            ver `esPersonal = !clienteId` en AribaApiTester.tsx: lo que se
            arma acá se guarda a la cuenta del usuario). Dentro de un
            proyecto (`embedded`, ver PruebasApisProyecto.tsx) sigue siendo
            "Pruebas APIs" — ahí sí es del proyecto/cliente, no personal. */}
        <header className="mb-12 mt-8 text-center md:text-left">
          <h1 className="text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 flex items-center justify-center md:justify-start gap-3">
            <Code2 className="w-10 h-10 text-blue-400" /> {embedded ? 'Pruebas APIs' : 'Mis APIs'}
          </h1>
          <p className="text-gray-400 mt-2 text-lg font-light max-w-2xl">
            Centro de pruebas y explorador interactivo para la validación, depuración e inyección de datos a través de APIs empresariales predefinidas.
          </p>
        </header>

        {/* Oculto de la vista a pedido — se mantiene montado (no se borra)
            porque su fetch de credenciales sigue alimentando el banner de
            AribaApiTester.tsx ("Credenciales activas" / Client ID / Data
            Center), que sí depende de este `onChange`. */}
        <div className="hidden">
          <CredencialesApiPanel
            clienteId={clienteId}
            clienteNombre={clienteNombre}
            credentials={credentials}
            onChange={onCredentialsChange}
          />
        </div>

        {/* PANEL DE ESTADÍSTICAS RÁPIDAS — oculto de la vista a pedido */}
        <section className="hidden grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-3xl flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-2xl text-blue-400">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-widest font-bold">Estado General</p>
              <h3 className="text-lg font-bold text-emerald-400 flex items-center gap-1.5 mt-0.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Online
              </h3>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-3xl flex items-center gap-4">
            <div className="p-3 bg-indigo-500/20 rounded-2xl text-indigo-400">
              <Code2 className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-widest font-bold">Módulos de Prueba</p>
              <h3 className="text-lg font-bold text-white mt-0.5">{SUB_APIS.filter((a) => a.status === 'active').length} Activos</h3>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-3xl flex items-center gap-4">
            <div className="p-3 bg-purple-500/20 rounded-2xl text-purple-400">
              <Activity className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-widest font-bold">Conexión Global</p>
              <h3 className="text-lg font-bold text-white mt-0.5">CIG / OData Habilitado</h3>
            </div>
          </div>
        </section>

        {/* BUSCADOR */}
        <div className="relative mb-10 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-4 py-4 text-white placeholder:text-gray-500 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm"
            placeholder="Buscar API por nombre o descripción..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* CONTENEDOR DE TARJETAS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredApis.map((api) => {
            const Icon = api.icon;
            return (
              <div
                key={api.id}
                onClick={() => setPage(api.targetPage)}
                className="group relative bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-[2.5rem] cursor-pointer hover:border-blue-500/50 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between min-h-[300px]"
              >
                {/* Indicador de estado */}
                <div className="absolute top-6 right-6">
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border ${
                      api.status === 'active'
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                        : api.status === 'beta'
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                        : 'bg-white/5 border-white/10 text-gray-400'
                    }`}
                  >
                    {api.statusText}
                  </span>
                </div>

                <div>
                  {/* Icono con gradiente */}
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 bg-gradient-to-br ${api.color} p-3.5 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-full h-full text-white" />
                  </div>

                  <h3 className="text-2xl font-bold mb-3 tracking-wide group-hover:text-blue-300 transition-colors">
                    {api.title}
                  </h3>
                  <p className="text-gray-400 text-sm font-light leading-relaxed">
                    {api.description}
                  </p>
                </div>

                {/* Botón interactivo inferior */}
                <div className="mt-8 flex items-center gap-2 text-sm font-bold text-blue-400 group-hover:text-blue-300 transition-colors">
                  Ingresar a pruebas
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            );
          })}

          {/* Tarjeta placeholder "Proximamente" */}
          <div className="flex flex-col items-center justify-center text-center p-8 rounded-[2.5rem] border-2 border-dashed border-white/15 text-gray-500 min-h-[300px]">
            <Sparkles className="w-10 h-10 text-gray-600 mb-4 animate-pulse" />
            <h3 className="text-lg font-bold text-gray-400 mb-1">Más integraciones</h3>
            <p className="text-sm text-gray-500 max-w-[200px] leading-relaxed">
              Salesforce, HubSpot, SAP ECC y ServiceNow se habilitarán próximamente.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
